import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createAdbRemoteInputQueue } from './adbRemoteInputQueue';
import {
  APP_ACTION_MAP,
  ADB_REMOTE_ACTION_MAP,
  ANDROID_TV_REMOTE_ACTION_MAP,
  getDefaultAvailabilityForMethod,
  getVisibleCommandsForMethod,
  resolveServerAction,
  WEBOS_ACTION_MAP,
} from '../Controls/tvCommandMappings';
import { getTvControlsIntegration, mergeIntegrationData, parseIntegrationData } from '../../utils/deviceIntegration';
import {
  countExternalInstalledApps,
  countLaunchableExternalApps,
  getOperationalInstalledApps,
  isSuitestreamInstalledApp,
  normalizeInstalledApps,
} from './installedAppSelection';
import {
  createPendingCommandAttempt,
  extractCommandAttempt,
} from './remoteCommandFeedback';

export const DEFAULT_HDMI_ASSIGNMENTS = [
  { port: 1, enabled: true, label: 'HDMI1' },
  { port: 2, enabled: true, label: 'HDMI2' },
  { port: 3, enabled: true, label: 'HDMI3' },
  { port: 4, enabled: true, label: 'HDMI4' },
];

export const HDMI_INPUT_VERIFICATION_DELAY_MS = 1000;
export const HDMI_INPUT_VERIFICATION_ATTEMPTS = 3;

const SOURCE_COMMAND_KEYS = new Set(['tv_home', 'hdmi_1', 'hdmi_2', 'hdmi_3', 'hdmi_4']);
const ADB_BATCHABLE_COMMANDS = new Set([
  'nav_up', 'nav_down', 'nav_left', 'nav_right', 'vol_up', 'vol_down', 'chan_up', 'chan_down',
]);
const NO_HDMI_PORTS = [];

export function isDiscreteSourceCommand(action) {
  const normalized = String(action || '').trim().toLowerCase();
  return normalized === 'input_tv_home' || /^input_hdmi_([1-9]|1[0-6])$/.test(normalized);
}

export function resolveSourceTargetDeviceId(sourceLayout, selectedDeviceId) {
  return Number(sourceLayout?.televisionDeviceId || selectedDeviceId || 0) || null;
}

export function resolveSelectedSourceDeviceId(sourceLayout, commandKey) {
  if (commandKey === 'tv_home') {
    return Number(sourceLayout?.televisionDeviceId || 0) || null;
  }
  const portMatch = String(commandKey || '').match(/^hdmi_(\d+)$/);
  if (!portMatch) return null;
  const source = (sourceLayout?.hdmiSources || [])
    .find((entry) => Number(entry.port) === Number(portMatch[1]));
  return Number(source?.playerDeviceId || 0) || null;
}

export function resolveWatchAction(watchTarget) {
  const explicitAction = String(watchTarget?.action || '').trim().toLowerCase();
  if (explicitAction === 'input_tv_home' || /^input_hdmi_([1-9]|1[0-6])$/.test(explicitAction)) {
    return explicitAction;
  }
  const port = Number(watchTarget?.port || 0);
  return Number.isInteger(port) && port >= 1 && port <= 16 ? `input_hdmi_${port}` : null;
}

const requestedHdmiPort = (commandKey) => {
  const match = String(commandKey || '').match(/^hdmi_(\d+)$/);
  return match ? Number(match[1]) : null;
};

export const evaluateHdmiInputVerification = (commandKey, state) => {
  const requestedPort = requestedHdmiPort(commandKey);
  const activeSource = state?.tvInputManager?.activeSource;
  const reportedPort = Number(activeSource?.port) || null;
  const evidence = activeSource?.adapter || activeSource?.source || 'TV input telemetry';

  if (!activeSource?.supported || !activeSource?.available || !reportedPort) {
    return {
      status: 'sent_unverified',
      requestedPort,
      reportedPort: null,
      message: requestedPort
        ? `HDMI ${requestedPort} command sent; this TV cannot verify its active input.`
        : 'Input command sent; this TV cannot verify its active input.',
    };
  }

  if (!requestedPort) {
    return {
      status: 'sent_unverified',
      requestedPort: null,
      reportedPort,
      message: `Input command sent; ${evidence} currently reports HDMI ${reportedPort}.`,
    };
  }

  if (reportedPort === requestedPort) {
    return {
      status: 'confirmed',
      requestedPort,
      reportedPort,
      message: `Confirmed on HDMI ${reportedPort} by ${evidence}.`,
    };
  }

  return {
    status: 'mismatch',
    requestedPort,
    reportedPort,
    message: `Mismatch: requested HDMI ${requestedPort}, but ${evidence} reports HDMI ${reportedPort}.`,
  };
};

export const METHOD_LABELS = {
  android_tv_remote: 'Android TV Remote',
  app: 'Suitestream App',
  assistant: 'Google Assistant',
  adb: 'ADB',
  adb_remote: 'ADB Remote',
  bluetooth: 'Bluetooth',
  cec: 'CEC/IR',
  cast: 'Chromecast',
  webos: 'LG TV Control',
  wol: 'Wake / Sleep on LAN',
};

export const PREVIEW_PRESETS = {
  high: {
    label: 'High',
    inputDelayMs: 0,
    backgroundIntervalMs: 10000,
  },
  medium: {
    label: 'Medium',
    inputDelayMs: 1000,
    backgroundIntervalMs: 20000,
  },
  low: {
    label: 'Low',
    inputDelayMs: 1500,
    backgroundIntervalMs: 30000,
  },
};

export const getScreenshotUnsupportedMessage = (error) => {
  if (error === 'take_screenshot_requires_api_30') {
    return 'Live preview requires Android 11 (API 30) or newer on this device.';
  }
  return '';
};

export const groupContentTargets = (targets) => {
  const groups = new Map();
  (Array.isArray(targets) ? targets : []).forEach((target) => {
    const key = target?.packageName || 'unknown';
    if (!groups.has(key)) {
      groups.set(key, {
        packageName: key,
        appName: target?.appName || target?.packageName || 'External app',
        targets: [],
      });
    }
    groups.get(key).targets.push(target);
  });
  return Array.from(groups.values());
};

export const isSuitestreamApp = (app) => {
  return isSuitestreamInstalledApp(app);
};

export const normalizeMacAddress = (value) => {
  const compact = String(value || '').replace(/[^a-fA-F0-9]/g, '').toUpperCase();
  if (compact.length !== 12) return null;
  const pairs = compact.match(/.{2}/g);
  return pairs ? pairs.join(':') : null;
};

export const formatDiscovery = (device) => {
  const values = Array.isArray(device?.discovery) ? device.discovery : [];
  return values.length ? values.join(', ') : 'unknown';
};

export const formatCapabilities = (device) => {
  const values = Array.isArray(device?.capabilities) ? device.capabilities : [];
  return values.length ? values.join(', ') : '—';
};

export const formatPorts = (device) => {
  const values = Array.isArray(device?.ports) ? device.ports : [];
  return values.length ? values.join(', ') : '—';
};

export const formatPairingStatus = (device) => {
  if (!device?.requiresPairing) return 'Not required';
  if (device?.paired || device?.pairingStatus === 'paired') return 'Paired';
  return 'Pending';
};

const formatResponse = (data) =>
  data?.stdout ?? data?.stderr ?? (data?.error ? `Error: ${data.error}` : JSON.stringify(data));

const detectMostlyBlackFrame = (dataUrl) =>
  new Promise((resolve) => {
    if (!dataUrl) {
      resolve(false);
      return;
    }

    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 24;
        canvas.height = 14;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) {
          resolve(false);
          return;
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
        let darkPixels = 0;
        let totalLuma = 0;

        for (let i = 0; i < data.length; i += 4) {
          const luma = (data[i] * 0.2126) + (data[i + 1] * 0.7152) + (data[i + 2] * 0.0722);
          totalLuma += luma;
          if (luma < 12) {
            darkPixels += 1;
          }
        }

        const pixelCount = data.length / 4;
        const darkRatio = pixelCount ? darkPixels / pixelCount : 0;
        const averageLuma = pixelCount ? totalLuma / pixelCount : 255;
        resolve(darkRatio >= 0.95 && averageLuma <= 10);
      } catch (err) {
        resolve(false);
      }
    };
    image.onerror = () => resolve(false);
    image.src = dataUrl;
  });

export function useSharedRemoteRuntime({
  device,
  onDeviceUpdated,
  onSourceTargetSelected,
  transport,
  previewEnabled = true,
  previewWindowVisible = false,
  nativePreviewForAdb = false,
  previewBackgroundIntervalOverride = null,
  adbInputBlocked = false,
  allowAdbRemote = false,
  adbHdmiPorts = NO_HDMI_PORTS,
}) {
  const commandIdRef = useRef(1);
  const latestCommandAttemptIdRef = useRef(null);
  const consoleIdRef = useRef(1);
  const snapshotTimeoutRef = useRef(null);
  const snapshotRequestInFlightRef = useRef(false);
  const pendingSnapshotReasonRef = useRef(null);

  const deviceId = device?.id || null;
  const deviceName = device?.name || 'Unknown Device';
  const integrationData = parseIntegrationData(device?.integration_data);
  const tvControlsIntegration = getTvControlsIntegration(device);
  const connectedMethods = Array.isArray(tvControlsIntegration?.connected_methods)
    ? tvControlsIntegration.connected_methods
    : [];
  const appControlMethods = Array.isArray(device?.appControlCapabilities?.availableMethods)
    ? device.appControlCapabilities.availableMethods
    : [];
  const liveMethods = Array.from(new Set([
    ...(Array.isArray(connectedMethods) ? connectedMethods : []),
    ...appControlMethods,
  ]));
  const lgTarget = device?.capabilities?.lgTarget || null;
  const androidTvRemote = device?.capabilities?.androidTvRemote || null;
  const androidTvRemoteReady = Boolean(androidTvRemote?.paired);
  const supportsApp = liveMethods.includes('accessibility') || liveMethods.includes('app') || Boolean(device?.is_app);
  const supportsAssistant = liveMethods.includes('assistant');
  const supportsADB = liveMethods.includes('adb');
  const supportsCast = liveMethods.includes('cast');
  const supportsBT = liveMethods.includes('bluetooth');
  const supportsCEC = liveMethods.includes('cec') || liveMethods.includes('ir');
  const isAppDevice = Boolean(device?.is_app);
  const supportsAppLauncher = Boolean(isAppDevice || device?.capabilities?.appLauncher);

  const baseMethodOptions = useMemo(
    () =>
      [
        { value: 'android_tv_remote', label: METHOD_LABELS.android_tv_remote, enabled: androidTvRemoteReady },
        { value: 'app', label: METHOD_LABELS.app, enabled: supportsApp },
        { value: 'assistant', label: METHOD_LABELS.assistant, enabled: supportsAssistant },
        { value: 'adb', label: METHOD_LABELS.adb, enabled: supportsADB },
        { value: 'adb_remote', label: METHOD_LABELS.adb_remote, visible: allowAdbRemote && device?.capabilities?.adbRemote?.configured, enabled: Boolean(
          allowAdbRemote && device?.capabilities?.adbRemote?.authorized && transport?.sendTargetAction) },
        { value: 'bluetooth', label: METHOD_LABELS.bluetooth, enabled: supportsBT },
        { value: 'cec', label: METHOD_LABELS.cec, enabled: supportsCEC },
        { value: 'cast', label: METHOD_LABELS.cast, enabled: supportsCast },
        { value: 'webos', label: METHOD_LABELS.webos, enabled: true },
        { value: 'wol', label: METHOD_LABELS.wol, enabled: true },
      ].filter((option) => option.enabled || option.visible),
    [allowAdbRemote, androidTvRemoteReady, supportsADB, supportsApp, supportsAssistant, supportsBT, supportsCEC, supportsCast,
      device?.capabilities?.adbRemote?.authorized, device?.capabilities?.adbRemote?.configured, transport?.sendTargetAction]
  );

  const [methodSelections, setMethodSelections] = useState({});
  // A saved Dashboard test method must not take over remotes without ADB controls.
  const storedMethod = deviceId ? localStorage.getItem(`tvRemoteMethod:${deviceId}`) : null;
  const savedMethod = storedMethod === 'adb_remote' && !allowAdbRemote ? null : storedMethod;
  const selectedMethod = methodSelections[deviceId] || (METHOD_LABELS[savedMethod] ? savedMethod : null)
    || (lgTarget ? 'webos' : null) || baseMethodOptions.find((option) => option.enabled)?.value || 'app';
  const setSelectedMethod = useCallback((method) => {
    if (!deviceId || !METHOD_LABELS[method] || (method === 'adb_remote' && !allowAdbRemote)) return;
    localStorage.setItem(`tvRemoteMethod:${deviceId}`, method);
    setMethodSelections((previous) => ({ ...previous, [deviceId]: method }));
  }, [allowAdbRemote, deviceId]);
  const methodOptions = baseMethodOptions.some((option) => option.value === selectedMethod)
    ? baseMethodOptions
    : [...baseMethodOptions, { value: selectedMethod, label: `${METHOD_LABELS[selectedMethod]} (unavailable)`, enabled: false }];
  const selectedMethodAvailable = baseMethodOptions.some((option) => option.value === selectedMethod && option.enabled);
  const adbInputQueueRef = useRef(null);
  if (!adbInputQueueRef.current) adbInputQueueRef.current = createAdbRemoteInputQueue();
  const adbInputEpochRef = useRef(0);
  const lgCancellationRef = useRef(null);
  const adbInputBlockedRef = useRef(adbInputBlocked);
  adbInputBlockedRef.current = adbInputBlocked;
  const activeScopeRef = useRef(null);
  const scopeGenerationRef = useRef(0);
  if (activeScopeRef.current !== `${deviceId}:${selectedMethod}`) scopeGenerationRef.current++;
  activeScopeRef.current = `${deviceId}:${selectedMethod}`;
  const commandScopesRef = useRef(new Map());
  const [availableByAction, setAvailableByAction] = useState(
    () => getDefaultAvailabilityForMethod(selectedMethod)
  );
  const [statusMessage, setStatusMessage] = useState('');
  const [lastAction, setLastAction] = useState('');
  const [lastCommandAttempt, setLastCommandAttempt] = useState(null);
  const [commandAttemptHistory, setCommandAttemptHistory] = useState([]);
  const [lastCommandKey, setLastCommandKey] = useState(null);
  const [appsStatus, setAppsStatus] = useState('idle');
  const [apps, setApps] = useState([]);
  const [selectedAppPackage, setSelectedAppPackage] = useState('');
  const [contentTargetsStatus, setContentTargetsStatus] = useState('idle');
  const [contentTargets, setContentTargets] = useState([]);
  const [targetExecution, setTargetExecution] = useState(null);
  const [wolMacInput, setWolMacInput] = useState('');
  const [hdmiAssignments, setHdmiAssignments] = useState(DEFAULT_HDMI_ASSIGNMENTS);
  const [selectedSource, setSelectedSource] = useState(DEFAULT_HDMI_ASSIGNMENTS[0].label);
  const [lgIpInput, setLgIpInput] = useState(tvControlsIntegration?.ip || '');
  const [lgMacInput, setLgMacInput] = useState(tvControlsIntegration?.mac || '');
  const [lgStatus, setLgStatus] = useState(tvControlsIntegration?.client_key ? 'paired' : 'unpaired');
  const [lgStatusMessage, setLgStatusMessage] = useState('');
  const [lgLastAction, setLgLastAction] = useState('');
  const [lgScanStatus, setLgScanStatus] = useState('idle');
  const [lgScanMessage, setLgScanMessage] = useState('');
  const [lgScanResults, setLgScanResults] = useState([]);
  const [lgScanRequestId, setLgScanRequestId] = useState(null);
  const [lgSaving, setLgSaving] = useState(false);
  const [wolSaving, setWolSaving] = useState(false);
  const [consoleLines, setConsoleLines] = useState([]);
  const [snapshotStatus, setSnapshotStatus] = useState('idle');
  const [snapshotMessage, setSnapshotMessage] = useState('');
  const [snapshotUnsupportedMessage, setSnapshotUnsupportedMessage] = useState('');
  const [liveSnapshot, setLiveSnapshot] = useState(null);
  const [snapshotCapturedAt, setSnapshotCapturedAt] = useState(null);
  const [previewPreset, setPreviewPreset] = useState('medium');
  const [inputDelayMs, setInputDelayMs] = useState(PREVIEW_PRESETS.medium.inputDelayMs);
  const [backgroundIntervalMs, setBackgroundIntervalMs] = useState(PREVIEW_PRESETS.medium.backgroundIntervalMs);
  const [isPageVisible, setIsPageVisible] = useState(typeof document === 'undefined' ? true : !document.hidden);
  const [blackFrameStreak, setBlackFrameStreak] = useState(0);

  const normalizedWolMac = useMemo(() => normalizeMacAddress(wolMacInput), [wolMacInput]);
  const normalizedLgMac = useMemo(() => normalizeMacAddress(lgMacInput), [lgMacInput]);
  const lgRemoteReady = selectedMethod === 'webos' && (lgTarget ? Boolean(lgTarget.paired) : lgStatus === 'connected');
  const supportsPreviewSnapshot =
    liveMethods.includes('accessibility')
    || liveMethods.includes('app');
  const canPreviewSnapshot = Boolean(previewEnabled && supportsPreviewSnapshot && (selectedMethod !== 'adb_remote' || nativePreviewForAdb));
  const effectiveBackgroundIntervalMs = Number.isFinite(previewBackgroundIntervalOverride)
    ? Math.max(0, Number(previewBackgroundIntervalOverride))
    : backgroundIntervalMs;
  const liveSnapshotLabel = useMemo(() => {
    if (!snapshotCapturedAt) {
      return 'No live preview yet';
    }
    return `Updated ${new Date(snapshotCapturedAt).toLocaleTimeString()}`;
  }, [snapshotCapturedAt]);
  const previewStorageKey = useMemo(
    () => (deviceId ? `tvRemotePreviewProfile:${deviceId}` : null),
    [deviceId]
  );
  const protectedVideoLikely = blackFrameStreak >= 3;
  const sourceLayout = device?.sourceLayout || null;
  const hdmiSources = Array.isArray(sourceLayout?.hdmiSources)
    ? sourceLayout.hdmiSources
    : null;
  const tvHomeSource = sourceLayout?.tvHomeSource === true;
  const watchTarget = sourceLayout?.watchTarget || null;

  const appendConsoleLine = useCallback((message, details = null) => {
    const entry = {
      id: consoleIdRef.current++,
      timestamp: new Date().toLocaleTimeString(),
      message,
      details,
    };
    setConsoleLines((prev) => [...prev.slice(-79), entry]);
  }, []);

  const recordCommandAttempt = useCallback((attempt) => {
    if (!attempt?.id) return;
    const scope = commandScopesRef.current.get(attempt.id);
    if (scope && (scope.key !== activeScopeRef.current || scope.generation !== scopeGenerationRef.current)) return;
    if (!latestCommandAttemptIdRef.current || latestCommandAttemptIdRef.current === attempt.id) {
      setLastCommandAttempt(attempt);
    }
    setCommandAttemptHistory((previous) => {
      const withoutCurrent = previous.filter((entry) => entry.id !== attempt.id);
      return [attempt, ...withoutCurrent].slice(0, 12);
    });
  }, []);

  const beginCommandAttempt = useCallback((action, transportName) => {
    const requestId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `remote-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    latestCommandAttemptIdRef.current = requestId;
    commandScopesRef.current.set(requestId, { key: activeScopeRef.current, generation: scopeGenerationRef.current });
    if (commandScopesRef.current.size > 100) commandScopesRef.current.delete(commandScopesRef.current.keys().next().value);
    recordCommandAttempt(createPendingCommandAttempt({
      id: requestId,
      action,
      transport: transportName,
      target: {
        deviceId,
        commandDeviceId: deviceId,
        name: deviceName,
      },
    }));
    return requestId;
  }, [deviceId, deviceName, recordCommandAttempt]);

  const recordCommandResult = useCallback((value) => {
    const attempt = extractCommandAttempt(value);
    if (attempt) recordCommandAttempt(attempt);
    return attempt;
  }, [recordCommandAttempt]);

  const recordDeliveryFailure = useCallback((error, requestId, action, method) => {
    if (recordCommandResult(error) || !requestId) return;
    const attempt = createPendingCommandAttempt({ id: requestId, action, transport: method,
      target: { deviceId, commandDeviceId: deviceId, name: deviceName } });
    recordCommandAttempt({ ...attempt, status: 'failed', completedAt: new Date().toISOString(),
      result: { code: 'delivery_uncertain', message: 'No correlated command result was received; delivery is unknown.' },
      failure: { stage: 'relay_response', code: 'delivery_uncertain', retryDisposition: 'no_replay_after_uncertain_delivery' },
      stages: { ...attempt.stages, server: { state: 'unknown', detail: 'Server response unavailable', latencyMs: null } },
    });
  }, [deviceId, deviceName, recordCommandAttempt, recordCommandResult]);

  const cancelRemoteInput = useCallback(() => {
    adbInputEpochRef.current += 1;
    adbInputQueueRef.current.clear();
    const cancel = lgCancellationRef.current;
    lgCancellationRef.current = null;
    cancel?.();
  }, []);

  useEffect(() => {
    const cancel = cancelRemoteInput;
    cancel();
    const hide = () => { if (document.hidden) cancel(); };
    window.addEventListener('blur', cancel);
    window.addEventListener('offline', cancel);
    window.addEventListener('pagehide', cancel);
    document.addEventListener('visibilitychange', hide);
    return () => {
      cancel();
      window.removeEventListener('blur', cancel);
      window.removeEventListener('offline', cancel);
      window.removeEventListener('pagehide', cancel);
      document.removeEventListener('visibilitychange', hide);
    };
  }, [deviceId, selectedMethod, selectedMethodAvailable, adbInputBlocked, cancelRemoteInput]);

  const handleRemoteInputEnd = useCallback((holdId) => {
    adbInputQueueRef.current.release(holdId);
  }, []);

  useEffect(() => {
    latestCommandAttemptIdRef.current = null;
    setLastCommandAttempt(null);
    setCommandAttemptHistory([]);
    setLastCommandKey(null);
  }, [deviceId]);

  useEffect(() => {
    const nextMac =
      tvControlsIntegration?.mac ||
      integrationData?.mac ||
      '';
    setWolMacInput(nextMac);
  }, [integrationData?.mac, tvControlsIntegration?.mac, deviceId]);

  useEffect(() => {
    setLgIpInput(tvControlsIntegration?.ip || '');
    setLgMacInput(tvControlsIntegration?.mac || '');
    setLgStatus(tvControlsIntegration?.client_key ? 'paired' : 'unpaired');
    setLgStatusMessage('');
    setLgLastAction('');
    setLgScanStatus('idle');
    setLgScanMessage('');
    setLgScanResults([]);
    setLgScanRequestId(null);
    setConsoleLines([]);
  }, [
    deviceId,
    tvControlsIntegration?.client_key,
    tvControlsIntegration?.ip,
    tvControlsIntegration?.mac,
  ]);

  useEffect(() => () => {
    if (snapshotTimeoutRef.current) {
      clearTimeout(snapshotTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    setSnapshotUnsupportedMessage('');
  }, [deviceId]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden || previewWindowVisible);
    };
    handleVisibilityChange();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [previewWindowVisible]);

  useEffect(() => {
    if (!previewStorageKey) return;
    const raw = localStorage.getItem(previewStorageKey);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      const preset = parsed?.preset && PREVIEW_PRESETS[parsed.preset] ? parsed.preset : 'medium';
      const nextInputDelay = Number.isFinite(parsed?.inputDelayMs)
        ? Math.max(0, Number(parsed.inputDelayMs))
        : PREVIEW_PRESETS[preset].inputDelayMs;
      const nextBackgroundInterval = Number.isFinite(parsed?.backgroundIntervalMs)
        ? Math.max(0, Number(parsed.backgroundIntervalMs))
        : PREVIEW_PRESETS[preset].backgroundIntervalMs;
      setPreviewPreset(preset);
      setInputDelayMs(nextInputDelay);
      setBackgroundIntervalMs(nextBackgroundInterval);
    } catch (err) {
      setPreviewPreset('medium');
      setInputDelayMs(PREVIEW_PRESETS.medium.inputDelayMs);
      setBackgroundIntervalMs(PREVIEW_PRESETS.medium.backgroundIntervalMs);
    }
  }, [previewStorageKey]);

  useEffect(() => {
    if (!previewStorageKey) return;
    localStorage.setItem(
      previewStorageKey,
      JSON.stringify({
        preset: previewPreset,
        inputDelayMs,
        backgroundIntervalMs,
      })
    );
  }, [backgroundIntervalMs, inputDelayMs, previewPreset, previewStorageKey]);

  useEffect(() => {
    if (!selectedMethod) return;
    setAvailableByAction(getDefaultAvailabilityForMethod(selectedMethod));
  }, [selectedMethod]);

  useEffect(() => {
    if (!device) return;
    if (device.hdmi_assignments) {
      try {
        const parsed =
          typeof device.hdmi_assignments === 'string'
            ? JSON.parse(device.hdmi_assignments)
            : device.hdmi_assignments;
        if (Array.isArray(parsed) && parsed.length > 0) {
          setHdmiAssignments(parsed);
        } else {
          setHdmiAssignments(DEFAULT_HDMI_ASSIGNMENTS);
        }
      } catch (err) {
        console.error('Error parsing hdmi_assignments', err);
        setHdmiAssignments(DEFAULT_HDMI_ASSIGNMENTS);
      }
    } else {
      setHdmiAssignments(DEFAULT_HDMI_ASSIGNMENTS);
    }

    setSelectedSource(
      device.source ||
        DEFAULT_HDMI_ASSIGNMENTS.find((assignment) => assignment.enabled)?.label ||
        'HDMI1'
    );
  }, [device]);

  const orderedApps = useMemo(() => {
    return getOperationalInstalledApps(apps);
  }, [apps]);
  const suitestreamApp = useMemo(
    () => normalizeInstalledApps(apps).find((app) => isSuitestreamApp(app)) || null,
    [apps]
  );
  const installedExternalAppCount = useMemo(
    () => countExternalInstalledApps(apps),
    [apps]
  );
  const launchableExternalAppCount = useMemo(
    () => countLaunchableExternalApps(apps),
    [apps]
  );
  const contentTargetGroups = useMemo(
    () => groupContentTargets(contentTargets),
    [contentTargets]
  );

  useEffect(() => {
    if (!orderedApps.length) {
      setSelectedAppPackage('');
      return;
    }
    const existing = orderedApps.find((app) => app.package_name === selectedAppPackage);
    if (!existing) {
      setSelectedAppPackage(orderedApps[0].package_name);
    }
  }, [orderedApps, selectedAppPackage]);

  const availableCommands = useMemo(
    () => getVisibleCommandsForMethod(selectedMethod, availableByAction),
    [availableByAction, selectedMethod]
  );

  const remoteAvailableCommands = useMemo(() => {
    if (!selectedMethodAvailable) return {};
    if (selectedMethod === 'adb_remote') {
      const available = { ...availableCommands };
      const ports = sourceLayout?.televisionDeviceId && Number(sourceLayout.televisionDeviceId) !== Number(deviceId)
        ? NO_HDMI_PORTS : adbHdmiPorts;
      for (const port of ports) {
        if (Number.isInteger(port) && port >= 1 && port <= 4) available[`hdmi_${port}`] = true;
      }
      return available;
    }
    if (selectedMethod === 'webos') {
      return lgRemoteReady ? Object.fromEntries(Object.keys(WEBOS_ACTION_MAP).map((key) => [key, true])) : {};
    }

    if (selectedMethod === 'wol') {
      return {};
    }

    if (selectedMethod !== 'assistant') {
      if (sourceLayout?.televisionDeviceId && Number(sourceLayout.televisionDeviceId) !== Number(deviceId)) {
        return { ...availableCommands, ...Object.fromEntries([...SOURCE_COMMAND_KEYS].map((key) => [key, false])) };
      }
      if (selectedMethod === 'app' && transport?.sendAndroidTvRemoteAction) {
        return { ...availableCommands, ...Object.fromEntries([...SOURCE_COMMAND_KEYS].map((key) => [key, true])) };
      }
      return availableCommands;
    }

    return {
      ...availableCommands,
      power: false,
    };
  }, [lgRemoteReady, adbHdmiPorts, availableCommands, selectedMethod, selectedMethodAvailable, sourceLayout?.televisionDeviceId, deviceId, transport?.sendAndroidTvRemoteAction]);

  const canPowerOn =
    selectedMethodAvailable && selectedMethod !== 'adb_remote' &&
    selectedMethod !== 'android_tv_remote' &&
    selectedMethod !== 'app' &&
    selectedMethod !== 'cast' &&
    selectedMethod !== 'webos' &&
    selectedMethod !== 'wol' &&
    Boolean(availableByAction.powerOn);
  const canPowerOff =
    selectedMethodAvailable && selectedMethod !== 'adb_remote' &&
    selectedMethod !== 'android_tv_remote' &&
    selectedMethod !== 'app' &&
    selectedMethod !== 'cast' &&
    selectedMethod !== 'webos' &&
    selectedMethod !== 'wol' &&
    Boolean(availableByAction.powerOff);
  const canSwitchSource = !selectedMethodAvailable || selectedMethod === 'adb_remote' ? false :
    selectedMethod === 'android_tv_remote'
      ? Boolean(ANDROID_TV_REMOTE_ACTION_MAP.input)
      : selectedMethod === 'app'
      ? Boolean(transport?.sendAndroidTvRemoteAction)
      : selectedMethod === 'webos'
        ? false
      : selectedMethod === 'wol'
        ? false
      : selectedMethod === 'cast'
        ? Boolean(selectedSource)
        : Boolean(selectedSource && availableByAction.inputSource);
  const showWolAction = selectedMethod !== 'webos' && selectedMethod !== 'wol' && Boolean(normalizedWolMac);

  const sendCommandViaSocket = useCallback(
    (command, value = null, requestId = null) => {
      if (!deviceId) return null;
      const id = commandIdRef.current++;
      if (typeof transport?.sendSocketCommand !== 'function') return null;
      return transport.sendSocketCommand({ deviceId, command, value, id, requestId }) ?? id;
    },
    [deviceId, transport]
  );

  const fetchLiveSnapshot = useCallback(
    async (reason = 'manual') => {
      if (
        !deviceId ||
        !canPreviewSnapshot ||
        !isPageVisible ||
        snapshotUnsupportedMessage ||
        typeof transport?.requestState !== 'function'
      ) {
        return;
      }
      if (snapshotRequestInFlightRef.current) {
        pendingSnapshotReasonRef.current = reason;
        return;
      }
      snapshotRequestInFlightRef.current = true;
      pendingSnapshotReasonRef.current = null;
      setSnapshotStatus((prev) => (prev === 'ready' ? 'refreshing' : 'loading'));
      try {
        const state = await transport.requestState({ deviceId, includeScreenshot: true });
        if (!state) {
          throw new Error('Snapshot response did not include state data.');
        }
        setLiveSnapshot(state);
        setSnapshotCapturedAt(Date.now());
        const unsupportedMessage = getScreenshotUnsupportedMessage(state.screenshotError);
        if (unsupportedMessage) {
          setSnapshotUnsupportedMessage(unsupportedMessage);
          setSnapshotStatus('unsupported');
          setSnapshotMessage(unsupportedMessage);
          return;
        }
        setSnapshotUnsupportedMessage('');
        setSnapshotStatus('ready');
        setSnapshotMessage(
          state.screenshot?.dataUrl
            ? reason === 'manual'
              ? 'Preview refreshed.'
              : `Preview refreshed after ${reason}.`
            : `State refreshed${state.screenshotError ? ` (${state.screenshotError})` : '.'}`
        );
      } catch (err) {
        const message = err?.response?.data?.message || err?.response?.data?.error || err.message || 'Snapshot failed';
        setSnapshotStatus('error');
        setSnapshotMessage(message);
      } finally {
        snapshotRequestInFlightRef.current = false;
        if (pendingSnapshotReasonRef.current) {
          const pendingReason = pendingSnapshotReasonRef.current;
          pendingSnapshotReasonRef.current = null;
          setTimeout(() => {
            fetchLiveSnapshot(pendingReason);
          }, 0);
        }
      }
    },
    [canPreviewSnapshot, deviceId, isPageVisible, snapshotUnsupportedMessage, transport]
  );

  const queueSnapshotRefresh = useCallback(
    (delayMs = inputDelayMs, reason = 'remote input') => {
      if (!canPreviewSnapshot) return;
      if (snapshotTimeoutRef.current) {
        clearTimeout(snapshotTimeoutRef.current);
      }
      snapshotTimeoutRef.current = setTimeout(() => {
        snapshotTimeoutRef.current = null;
        fetchLiveSnapshot(reason);
      }, delayMs);
    },
    [canPreviewSnapshot, fetchLiveSnapshot, inputDelayMs]
  );

  const loadApps = useCallback(async () => {
    if (!deviceId || !supportsAppLauncher || typeof transport?.loadApps !== 'function') return;
    setAppsStatus('loading');
    try {
      const nextApps = await transport.loadApps({ deviceId });
      setApps(normalizeInstalledApps(nextApps));
      setAppsStatus('ready');
    } catch (err) {
      setAppsStatus('error');
      setStatusMessage(err?.response?.data?.message || err.message || 'Failed to load apps');
    }
  }, [deviceId, supportsAppLauncher, transport]);

  const loadContentTargets = useCallback(async () => {
    if (!deviceId || !supportsAppLauncher || typeof transport?.loadContentTargets !== 'function') return;
    setContentTargetsStatus('loading');
    try {
      const nextTargets = await transport.loadContentTargets({ deviceId });
      setContentTargets(Array.isArray(nextTargets) ? nextTargets : []);
      setContentTargetsStatus('ready');
    } catch (err) {
      setContentTargets([]);
      setContentTargetsStatus('error');
      setStatusMessage(err?.response?.data?.message || err.message || 'Failed to load shortcuts');
    }
  }, [deviceId, supportsAppLauncher, transport]);

  useEffect(() => {
    setApps([]);
    setContentTargets([]);
    setTargetExecution(null);
    if (!deviceId || !supportsAppLauncher) return;
    loadApps();
    loadContentTargets();
  }, [deviceId, supportsAppLauncher, loadApps, loadContentTargets]);

  useEffect(() => {
    if (!canPreviewSnapshot || !deviceId || !isPageVisible) {
      setLiveSnapshot(null);
      setSnapshotCapturedAt(null);
      setSnapshotStatus('idle');
      setSnapshotMessage('');
      return undefined;
    }

    if (snapshotUnsupportedMessage) {
      return undefined;
    }

    fetchLiveSnapshot('panel open');
    if (effectiveBackgroundIntervalMs <= 0) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      fetchLiveSnapshot('background refresh');
    }, effectiveBackgroundIntervalMs);

    return () => {
      clearInterval(intervalId);
    };
  }, [
    canPreviewSnapshot,
    deviceId,
    effectiveBackgroundIntervalMs,
    fetchLiveSnapshot,
    isPageVisible,
    snapshotUnsupportedMessage,
  ]);

  useEffect(() => {
    let cancelled = false;
    const dataUrl = liveSnapshot?.screenshot?.dataUrl || null;
    if (!dataUrl) {
      setBlackFrameStreak(0);
      return undefined;
    }

    detectMostlyBlackFrame(dataUrl).then((isMostlyBlack) => {
      if (cancelled) return;
      setBlackFrameStreak((prev) => (isMostlyBlack ? prev + 1 : 0));
    });

    return () => {
      cancelled = true;
    };
  }, [liveSnapshot?.screenshot?.dataUrl]);

  useEffect(() => {
    if (!deviceId || typeof transport?.subscribe !== 'function') return undefined;

    const cleanup = transport.subscribe({
      deviceId,
      onResponse: (payload) => {
        const type = payload.type || null;

        if (type === 'network_scan_response') {
          const payloadRequestId = payload.requestId ? String(payload.requestId) : null;
          if (lgScanRequestId && payloadRequestId && payloadRequestId !== String(lgScanRequestId)) return;
          if (payload.error) {
            setLgScanStatus('error');
            setLgScanMessage('Scan failed. Check device permissions or network.');
            appendConsoleLine('LG network scan failed.', payload);
            return;
          }
          const list = Array.isArray(payload.devices) ? payload.devices : [];
          setLgScanResults(list);
          setLgScanStatus('done');
          setLgScanMessage(list.length ? `Found ${list.length} device(s).` : 'No devices found.');
          appendConsoleLine(`LG network scan completed with ${list.length} result(s).`, payload);
          return;
        }

        if (type === 'tv_remote_status') {
          const nextStatus = String(payload.status || '').toLowerCase();
          if (nextStatus) setLgStatus(nextStatus);
          if (payload.message) setLgStatusMessage(String(payload.message));
          appendConsoleLine('LG TV status update received.', payload);
          return;
        }

        if (type === 'tv_remote_connect_response') {
          if (payload.status) setLgStatus(String(payload.status).toLowerCase());
          if (payload.message) setLgStatusMessage(String(payload.message));
          appendConsoleLine('LG TV connect/pair response received.', payload);
          if ((payload.clientKey || payload.client_key) && typeof onDeviceUpdated === 'function') {
            const clientKey = payload.clientKey || payload.client_key;
            const mergedIntegration = mergeIntegrationData(device?.integration_data, {
              supports_webos: true,
              tv_controls: {
                ...(getTvControlsIntegration(device) || {}),
                ip: lgIpInput.trim() || undefined,
                mac: normalizedLgMac || undefined,
                client_key: clientKey,
              },
            });
            onDeviceUpdated({
              ...device,
              integration_data: mergedIntegration,
            });
          }
          return;
        }

        if (type === 'tv_remote_disconnect_response') {
          setLgStatus('disconnected');
          if (payload.message) setLgStatusMessage(String(payload.message));
          appendConsoleLine('LG TV disconnect response received.', payload);
          return;
        }

        if (type === 'remote_action_response') {
          recordCommandResult(payload);
          const nextMessage = payload.success
            ? 'Suitestream App command handled.'
            : `Suitestream App command failed: ${payload.error || 'unknown'}`;
          setStatusMessage(nextMessage);
          appendConsoleLine('Suitestream App action response received.', payload);
          return;
        }

        if (type === 'tv_remote_action_response') {
          recordCommandResult(payload);
          const nextMessage = payload.success
            ? 'LG TV command sent.'
            : `LG TV command failed: ${payload.error || 'unknown'}`;
          setLgStatusMessage(nextMessage);
          appendConsoleLine('LG TV action response received.', payload);
          return;
        }

        if (type === 'list_apps_response') {
          setStatusMessage('Apps refreshed from the device.');
          appendConsoleLine('Installed app list refreshed from the device.', payload);
          setTimeout(() => loadApps(), 400);
          return;
        }

        if (type === 'launch_app_response') {
          setStatusMessage(payload.success ? 'App launched.' : `Launch failed: ${payload.error || 'unknown'}`);
          appendConsoleLine('Launch app response received.', payload);
          return;
        }

        if (type === 'launch_suitestream_response') {
          setStatusMessage(payload.success ? 'Suitestream launched.' : 'Suitestream launch failed.');
          appendConsoleLine('Launch Suitestream response received.', payload);
          return;
        }

        if (type === 'wake_on_lan_response') {
          const target = payload.mac || payload.normalizedMac || normalizedWolMac || 'device';
          setStatusMessage(
            payload.success
              ? `Power on sent to ${target}.`
              : `Wake-on-LAN failed: ${payload.error || 'unknown'}`
          );
          appendConsoleLine('Wake-on-LAN response received.', payload);
          return;
        }

        if (type === 'sleep_on_lan_response') {
          const target = payload.mac || payload.normalizedMac || normalizedWolMac || 'device';
          setStatusMessage(
            payload.success
              ? `Sleep packet sent to ${target}.`
              : `Sleep-on-LAN failed: ${payload.error || 'unknown'}`
          );
          appendConsoleLine('Sleep-on-LAN response received.', payload);
        }
      },
      onMissing: (payload) => {
        setStatusMessage(
          payload.reason === 'native_channel_missing'
            ? 'The device native command channel is not connected.'
            : 'Device not connected.'
        );
        appendConsoleLine('Device not connected for requested command.', payload);
      },
    });

    return typeof cleanup === 'function' ? cleanup : undefined;
  }, [
    appendConsoleLine,
    device,
    deviceId,
    lgIpInput,
    lgScanRequestId,
    loadApps,
    normalizedLgMac,
    normalizedWolMac,
    onDeviceUpdated,
    recordCommandResult,
    transport,
  ]);

  const persistLgIntegration = useCallback(
    async (patch = {}, successMessage = '') => {
      if (!deviceId || typeof transport?.updateIntegration !== 'function') return false;
      setLgSaving(true);
      try {
        await transport.updateIntegration({ deviceId, ...patch });

        const nextIntegrationPatch = {};
        if (patch.supportsWebos !== undefined) {
          nextIntegrationPatch.supports_webos = Boolean(patch.supportsWebos);
        }
        if (patch.tvControls && typeof patch.tvControls === 'object') {
          nextIntegrationPatch.tv_controls = {
            ...(getTvControlsIntegration(device) || {}),
            ...patch.tvControls,
          };
        }

        if (Object.keys(nextIntegrationPatch).length > 0 && typeof onDeviceUpdated === 'function') {
          onDeviceUpdated({
            ...device,
            integration_data: mergeIntegrationData(device?.integration_data, nextIntegrationPatch),
          });
        }

        if (successMessage) {
          setStatusMessage(successMessage);
          appendConsoleLine(successMessage, patch);
        }
        return true;
      } catch (err) {
        const message = err?.response?.data?.message || err.message;
        setStatusMessage(`LG TV Control save failed: ${message}`);
        appendConsoleLine('LG TV Control save failed.', { error: message, patch });
        return false;
      } finally {
        setLgSaving(false);
      }
    },
    [appendConsoleLine, device, deviceId, onDeviceUpdated, transport]
  );

  const persistWolMac = useCallback(
    async () => {
      if (!deviceId || typeof transport?.updateIntegration !== 'function') return false;
      if (!normalizedWolMac) {
        setStatusMessage('Enter a valid MAC address (12 hex digits).');
        return false;
      }

      setWolSaving(true);
      try {
        await transport.updateIntegration({ deviceId, bluetoothMac: normalizedWolMac });

        const nextIntegrationPatch =
          device?.type === 'lgtv'
            ? {
                tv_controls: {
                  ...(getTvControlsIntegration(device) || {}),
                  mac: normalizedWolMac,
                },
              }
            : {
                mac: normalizedWolMac,
              };

        if (typeof onDeviceUpdated === 'function') {
          onDeviceUpdated({
            ...device,
            integration_data: mergeIntegrationData(device?.integration_data, nextIntegrationPatch),
          });
        }

        setStatusMessage(`Saved Wake / Sleep on LAN MAC: ${normalizedWolMac}.`);
        appendConsoleLine('Saved Wake / Sleep on LAN MAC address.', { mac: normalizedWolMac });
        return true;
      } catch (err) {
        const message = err?.response?.data?.message || err.message;
        setStatusMessage(`Wake / Sleep on LAN save failed: ${message}`);
        appendConsoleLine('Wake / Sleep on LAN save failed.', { error: message });
        return false;
      } finally {
        setWolSaving(false);
      }
    },
    [appendConsoleLine, device, deviceId, normalizedWolMac, onDeviceUpdated, transport]
  );

  const sendServerCommand = useCallback(
    async (actionKey, method, requestId = null) => {
      if (!actionKey || typeof transport?.sendProtocolCommand !== 'function') return;
      try {
        const response = await transport.sendProtocolCommand({
          deviceId,
          command: actionKey,
          protocol: method,
          requestId,
        });
        recordCommandResult(response);
        const message = formatResponse(response?.result || response);
        setStatusMessage(message);
        appendConsoleLine(`${METHOD_LABELS[method] || method} command response received.`, {
          actionKey,
          method,
          response,
        });
      } catch (err) {
        recordCommandResult(err);
        const responseMessage = err?.response?.data?.error || err?.response?.data?.message || err.message;
        const message = `${METHOD_LABELS[method] || method} error: ${responseMessage}`;
        setStatusMessage(message);
        appendConsoleLine(`${METHOD_LABELS[method] || method} command failed.`, {
          actionKey,
          method,
          error: err?.response?.data || responseMessage,
        });
      }
    },
    [appendConsoleLine, deviceId, recordCommandResult, transport]
  );

  const sendCastCommand = useCallback(
    async (actionKey, requestId = null) => {
      if (!actionKey || typeof transport?.sendProtocolCommand !== 'function') return;
      try {
        const response = await transport.sendProtocolCommand({
          deviceId,
          command: actionKey,
          protocol: 'cast',
          requestId,
        });
        recordCommandResult(response);
        const message = formatResponse(response?.result || response);
        setStatusMessage(message);
        appendConsoleLine('Chromecast command response received.', {
          actionKey,
          response,
        });
      } catch (err) {
        recordCommandResult(err);
        const message = `Chromecast error: ${err.message}`;
        setStatusMessage(message);
        appendConsoleLine('Chromecast command failed.', {
          actionKey,
          error: err?.response?.data || err.message,
        });
      }
    },
    [appendConsoleLine, deviceId, recordCommandResult, transport]
  );

  const sendAssistantCommand = useCallback(
    async (actionKey, params = {}, requestId = null) => {
      if (!actionKey || typeof transport?.sendAssistantCommand !== 'function') return;
      try {
        const response = await transport.sendAssistantCommand({
          deviceId,
          command: actionKey,
          params,
          requestId,
        });
        recordCommandResult(response);
        const responseText =
          typeof response?.response === 'string' ? response.response.trim() : '';
        const message = responseText || 'Google Assistant command sent.';
        setStatusMessage(message);
        appendConsoleLine('Google Assistant command response received.', {
          actionKey,
          params,
          response,
        });
      } catch (err) {
        recordCommandResult(err);
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err.message;
        setStatusMessage(`Google Assistant error: ${message}`);
        appendConsoleLine('Google Assistant command failed.', {
          actionKey,
          params,
          error: err?.response?.data || message,
        });
      }
    },
    [appendConsoleLine, deviceId, recordCommandResult, transport]
  );

  const sendAppCommand = useCallback(
    async (action, requestId = null) => {
      if (!action) return;
      if (typeof transport?.sendRoutedAction === 'function') {
        try {
          const result = await transport.sendRoutedAction({ deviceId, action, requestId, controlMethod: 'app' });
          recordCommandResult(result);
          const route = result?.decision?.route;
          setStatusMessage(
            route
              ? `Sent via ${route.transport}: ${route.command}`
              : 'Suitestream App command sent.'
          );
          appendConsoleLine('Foreground-aware app command response received.', {
            action,
            decision: result?.decision || null,
            response: result?.response || null,
          });
          return result;
        } catch (err) {
          recordCommandResult(err);
          const decision = err?.response?.data?.decision || err?.payload?.decision || null;
          const reason = decision?.reason || err?.response?.data?.error || err.message;
          setStatusMessage(`App command blocked: ${reason}`);
          appendConsoleLine('Foreground-aware app command blocked.', {
            action,
            decision,
            error: err?.response?.data || err?.payload || err.message,
          });
          return null;
        }
      }
      const socketRequestId = sendCommandViaSocket('remote_action', { key: action }, requestId);
      setStatusMessage('Suitestream App command sent.');
      appendConsoleLine('Suitestream App command sent.', { action, requestId: socketRequestId });
      return { requestId: socketRequestId };
    },
    [appendConsoleLine, deviceId, recordCommandResult, sendCommandViaSocket, transport]
  );

  const sendAndroidTvRemoteCommand = useCallback(
    async (action, requestId = null, targetDeviceId = deviceId) => {
      if (!action || typeof transport?.sendAndroidTvRemoteAction !== 'function') return null;
      try {
        const result = await transport.sendAndroidTvRemoteAction({
          deviceId: targetDeviceId,
          action,
          requestId,
          controlMethod: 'android_tv_remote_v2',
        });
        recordCommandResult(result);
        setStatusMessage('Android TV Remote command sent.');
        appendConsoleLine('Android TV Remote action response received.', {
          action,
          response: result,
        });
        return result;
      } catch (err) {
        recordDeliveryFailure(err, requestId, action, 'android_tv_remote');
        const message = err?.response?.data?.message || err?.payload?.message || err.message;
        appendConsoleLine('Android TV Remote command failed.', {
          action,
          error: err?.response?.data || err?.payload || message,
        });
        setStatusMessage(`Android TV Remote failed: ${message}`);
        return null;
      }
    },
    [appendConsoleLine, deviceId, recordCommandResult, recordDeliveryFailure, transport]
  );

  const sendWebosCommand = useCallback(
    async (actionKey, requestId = null, operationParams = {}, onResult = null) => {
      if (!actionKey) return false;
      const scope = `${deviceId}:${selectedMethod}`;
      const epoch = adbInputEpochRef.current;
      if (lgTarget && transport?.sendTargetAction) {
        const cancel = () => { void transport.sendTargetAction({ deviceId, action: 'lg_op_stop',
          requestId: `${requestId}.stop`, controlMethod: 'lg_webos_remote' }).catch(() => {}); };
        lgCancellationRef.current = cancel;
        if (['connect', 'lg_op_reconnect'].includes(actionKey)) setLgStatus('connecting');
        try {
          const result = await transport.sendTargetAction({ deviceId, action: actionKey, requestId,
            controlMethod: 'lg_webos_remote', operationParams });
          recordCommandResult(result);
          if (activeScopeRef.current !== scope || adbInputEpochRef.current !== epoch) return false;
          onResult?.(result);
          const deliveryStage = result.attempt?.diagnostics?.deliveryStage;
          setLgStatus(deliveryStage === 'packet_written' ? 'wake unverified'
            : deliveryStage === 'cancelled_unsent' ? 'stopped'
              : ['power_off', 'lg_op_off'].includes(actionKey) ? 'off requested; power unverified'
                : ['connected', 'written', 'acknowledged'].includes(deliveryStage) ? 'ready' : 'delivery unverified');
          setLgLastAction(actionKey);
          const delivery = result.attempt?.diagnostics?.deliveryStage;
          setLgStatusMessage({
            connected: 'Ready: authenticated LG session connected; visible state unverified.',
            acknowledged: 'TV acknowledged the API request; visible effect unverified.',
            written: 'Pointer written; visible effect unverified.',
            packet_written: 'WOL packet emitted; TV recovery unverified.',
          }[delivery] || 'Relay returned without delivery evidence; visible effect unverified.');
          if (result.attempt?.historyUnavailable) setLgStatusMessage((message) => `${message} Diagnostic history could not be retained.`);
          return true;
        } catch (error) {
          recordDeliveryFailure(error, requestId, actionKey, 'lg_webos_remote');
          if (activeScopeRef.current === scope && adbInputEpochRef.current === epoch) {
            const reason = error.response?.data?.message || 'LG delivery failed or is uncertain. No automatic retry.';
            setLgStatus(/relay|eligible/.test(reason) ? 'relay unavailable' : /permission|identity|tls|pair/i.test(reason) ? 'permission / identity error' : 'TV unreachable or delivery uncertain');
            setLgStatusMessage(reason);
          }
          return false;
        } finally { if (lgCancellationRef.current === cancel) lgCancellationRef.current = null; }
      }
      sendCommandViaSocket('tv_remote_action', { actionKey, phase: 'start', mode: 'lgwebos' }, requestId);
      return true;
    },
    [deviceId, selectedMethod, lgTarget, transport, recordCommandResult, recordDeliveryFailure, sendCommandViaSocket]
  );

  const handleCommand = useCallback(
    async (commandKey, input = {}) => {
      if (activeScopeRef.current !== `${deviceId}:${selectedMethod}`) return;
      if (selectedMethod !== 'adb_remote') setStatusMessage('');
      if (!commandKey || !selectedMethod) return;
      setLastCommandKey(commandKey);

      if (commandKey === 'bluetooth_settings' && typeof transport?.openBluetoothSettings === 'function') {
        const requestId = beginCommandAttempt('open_bluetooth_settings', 'native_android');
        try {
          const result = await transport.openBluetoothSettings({ deviceId, requestId });
          recordCommandResult(result);
          setLastAction('open_bluetooth_settings');
          setStatusMessage('Bluetooth settings requested. The TV may fall back to general Settings.');
          appendConsoleLine('Bluetooth settings request sent.', { response: result });
        } catch (error) {
          recordCommandResult(error);
          const message = error?.response?.data?.message || error?.payload?.message || error.message;
          setStatusMessage(`Bluetooth settings failed: ${message}`);
          appendConsoleLine('Bluetooth settings request failed.', { error: message });
        }
        return;
      }

      if (!selectedMethodAvailable) {
        setStatusMessage(`${METHOD_LABELS[selectedMethod]} is unavailable for this target. Choose a method explicitly to change routes.`);
        return;
      }
      if (selectedMethod === 'adb_remote') {
        if (adbInputBlockedRef.current) {
          setStatusMessage('ADB keys are paused while the reboot request is being reviewed or processed.');
          return;
        }
        const hdmiMatch = /^hdmi_([1-4])$/.exec(commandKey);
        const hdmiAction = hdmiMatch && adbHdmiPorts.includes(Number(hdmiMatch[1]))
          && (!sourceLayout?.televisionDeviceId || Number(sourceLayout.televisionDeviceId) === Number(deviceId))
          ? `KEYCODE_TV_INPUT_HDMI_${hdmiMatch[1]}` : null;
        const action = ADB_REMOTE_ACTION_MAP[commandKey] || hdmiAction;
        if (!action) {
          setStatusMessage('That command is not supported by ADB Remote.');
          return;
        }
        const scope = activeScopeRef.current;
        const epoch = adbInputEpochRef.current;
        const isCurrent = () => scope === activeScopeRef.current && epoch === adbInputEpochRef.current;
        if (document.hidden || navigator.onLine === false) {
          setStatusMessage('ADB input paused while this page is hidden or offline.');
          return;
        }
        return adbInputQueueRef.current.enqueue({
          holdId: input.holdId,
          repeat: input.repeat === true,
          batchKey: ADB_BATCHABLE_COMMANDS.has(commandKey) && input.repeat !== true ? action : null,
          isCurrent,
          onDiscard: (reason) => {
            if (!isCurrent() || !['expired', 'overflow'].includes(reason)) return;
            setStatusMessage(reason === 'expired'
              ? 'Waiting ADB input expired; the remaining sequence was cleared.'
              : 'ADB input was too fast; the waiting sequence was cleared.');
            appendConsoleLine('Waiting ADB input cleared.', { reason });
          },
          send: async (repeat) => {
            setStatusMessage('');
            const requestId = beginCommandAttempt(action, selectedMethod);
            try {
              const result = await transport.sendTargetAction({ deviceId, action, requestId, controlMethod: 'adb_remote',
                ...(repeat > 1 ? { repeat } : {}) });
              if (!isCurrent()) return false;
              const attempt = recordCommandResult(result);
              if (result?.success === false || ['failed', 'timed_out'].includes(attempt?.status)) {
                setStatusMessage(attempt?.result?.message || 'ADB command failed; waiting input cleared.');
                return false;
              }
              appendConsoleLine('ADB Remote command result.', { requestId, repeat, attempt: result?.attempt || null });
              const relayId = result?.attempt?.route?.bridgeDeviceId || result?.bridgeDeviceId;
              setStatusMessage(`ADB command completed${repeat > 1 ? ` (${repeat} taps)` : ''}${relayId ? ` via relay ${relayId}` : ''}; visible effect unverified.`);
              setLastAction(action);
              return true;
            } catch (error) {
              if (!isCurrent()) return false;
              recordDeliveryFailure(error, requestId, action, 'adb_remote');
              appendConsoleLine('ADB Remote command failed.', { requestId, attempt: extractCommandAttempt(error) });
              setStatusMessage(error?.response?.data?.message || error.message || 'ADB command failed.');
              return false;
            }
          },
        });
      }

      if (selectedMethod === 'app') {
        const action = APP_ACTION_MAP[commandKey];
        if (!action) {
          setStatusMessage('That command is not available through the Suitestream App transport.');
          return;
        }
        const requestId = beginCommandAttempt(action, selectedMethod);
        const result = await sendAppCommand(action, requestId);
        if (!result) return;
        setLastAction(action);
        queueSnapshotRefresh(inputDelayMs, 'remote input');
        return;
      }

      if (selectedMethod === 'android_tv_remote') {
        const action = ANDROID_TV_REMOTE_ACTION_MAP[commandKey] || APP_ACTION_MAP[commandKey];
        if (!action) {
          setStatusMessage('That command is not mapped for Android TV Remote.');
          return;
        }
        const requestId = beginCommandAttempt(action, selectedMethod);
        const result = await sendAndroidTvRemoteCommand(action, requestId);
        if (!result) return;
        setLastAction(action);
        queueSnapshotRefresh(inputDelayMs, 'remote input');
        return;
      }

      if (selectedMethod === 'webos') {
        if (!lgRemoteReady) {
          setStatusMessage('Connect or pair with the LG TV before sending WebOS commands.');
          return;
        }
        const action = WEBOS_ACTION_MAP[commandKey] || (/^lg_(key|custom|op)_/.test(commandKey) ? commandKey : null);
        if (!action) {
          setStatusMessage('That command is not mapped for LG TV Control.');
          return;
        }
        const epoch = adbInputEpochRef.current;
        const scope = `${deviceId}:${selectedMethod}`;
        return adbInputQueueRef.current.enqueue({
          isCurrent: () => activeScopeRef.current === scope && adbInputEpochRef.current === epoch,
          repeat: Boolean(input.repeat), holdId: input.holdId,
          send: async () => {
            const requestId = beginCommandAttempt(action, 'lg_webos_remote');
            return sendWebosCommand(action, requestId, input.operationParams, input.onResult);
          },
        });
      }

      const action = resolveServerAction(commandKey, availableByAction);
      if (!action) {
        setStatusMessage('That command is not available for the selected method.');
        return;
      }

      const requestId = beginCommandAttempt(action, selectedMethod);

      if (selectedMethod === 'assistant') {
        await sendAssistantCommand(action, {}, requestId);
      } else if (selectedMethod === 'cast') {
        await sendCastCommand(action, requestId);
      } else {
        await sendServerCommand(action, selectedMethod, requestId);
      }
      setLastAction(action);
      queueSnapshotRefresh(inputDelayMs, 'remote input');
    },
    [
      availableByAction,
      adbHdmiPorts,
      sourceLayout?.televisionDeviceId,
      appendConsoleLine,
      beginCommandAttempt,
      deviceId,
      inputDelayMs,
      lgStatus, lgRemoteReady,
      queueSnapshotRefresh,
      recordCommandResult,
      selectedMethod,
      selectedMethodAvailable,
      recordDeliveryFailure,
      sendAppCommand,
      sendAndroidTvRemoteCommand,
      sendAssistantCommand,
      sendCastCommand,
      sendServerCommand,
      sendWebosCommand,
      transport,
    ]
  );

  const retryLastCommand = useCallback(() => {
    if (!lastCommandKey) return;
    handleCommand(lastCommandKey);
  }, [handleCommand, lastCommandKey]);

  const handlePreviewPresetChange = useCallback((value) => {
    const preset = PREVIEW_PRESETS[value];
    if (!preset) return;
    setPreviewPreset(value);
    setInputDelayMs(preset.inputDelayMs);
    setBackgroundIntervalMs(preset.backgroundIntervalMs);
  }, []);

  const handleInputDelayChange = useCallback((value) => {
    const nextValue = Math.max(0, Number(value || 0));
    setInputDelayMs(nextValue);
    setPreviewPreset('custom');
  }, []);

  const handleBackgroundIntervalChange = useCallback((value) => {
    const nextValue = Math.max(0, Number(value || 0));
    setBackgroundIntervalMs(nextValue);
    setPreviewPreset('custom');
  }, []);

  const handlePowerOn = useCallback(async () => {
    if (!selectedMethodAvailable || ['cast', 'app', 'adb_remote'].includes(selectedMethod)) {
      setStatusMessage(`${METHOD_LABELS[selectedMethod]} does not support power on.`);
      return;
    }
    appendConsoleLine('Power On requested.', { method: selectedMethod });
    if (selectedMethod === 'assistant') {
      await sendAssistantCommand('powerOn');
    } else {
      await sendServerCommand('powerOn', selectedMethod);
    }
    setLastAction('powerOn');
  }, [appendConsoleLine, selectedMethod, selectedMethodAvailable, sendAssistantCommand, sendServerCommand]);

  const handlePowerOff = useCallback(async () => {
    if (!selectedMethodAvailable || ['cast', 'app', 'adb_remote'].includes(selectedMethod)) {
      setStatusMessage(`${METHOD_LABELS[selectedMethod]} does not support power off.`);
      return;
    }
    appendConsoleLine('Power Off requested.', { method: selectedMethod });
    if (selectedMethod === 'assistant') {
      await sendAssistantCommand('powerOff');
    } else {
      await sendServerCommand('powerOff', selectedMethod);
    }
    setLastAction('powerOff');
  }, [appendConsoleLine, selectedMethod, selectedMethodAvailable, sendAssistantCommand, sendServerCommand]);

  const handleWakeOnLan = useCallback(() => {
    if (!normalizedWolMac) {
      setStatusMessage('Enter a valid MAC address (12 hex digits).');
      return;
    }
    sendCommandViaSocket('wake_on_lan', { mac: normalizedWolMac });
    setStatusMessage(`Power on request sent for ${normalizedWolMac}.`);
    setLastAction(`wake_on_lan:${normalizedWolMac}`);
    appendConsoleLine('Wake-on-LAN command sent.', { mac: normalizedWolMac });
  }, [appendConsoleLine, normalizedWolMac, sendCommandViaSocket]);

  const handleSleepOnLan = useCallback(() => {
    if (!normalizedWolMac) {
      setStatusMessage('Enter a valid MAC address (12 hex digits).');
      return;
    }
    sendCommandViaSocket('sleep_on_lan', { mac: normalizedWolMac });
    setStatusMessage(`Sleep packet request sent for ${normalizedWolMac}.`);
    setLastAction(`sleep_on_lan:${normalizedWolMac}`);
    appendConsoleLine('Sleep-on-LAN command sent.', { mac: normalizedWolMac });
  }, [appendConsoleLine, normalizedWolMac, sendCommandViaSocket]);

  const handleLaunchSuitestream = useCallback(() => {
    sendCommandViaSocket('launch_suitestream');
    setStatusMessage('Launching Suitestream…');
    setLastAction('launch_suitestream');
  }, [sendCommandViaSocket]);

  const launchAppPackage = useCallback(async (packageName) => {
    if (typeof transport?.launchApp === 'function') {
      try {
        const result = await transport.launchApp({ deviceId, packageName });
        setStatusMessage(
          result?.automationRun
            ? 'App launched; automatic startup is running.'
            : 'App launched.'
        );
        setLastAction(`launch_app:${packageName}`);
        return;
      } catch (error) {
        const status = error?.response?.status || error?.status;
        if (status !== 404) {
          setStatusMessage(
            `App launch failed: ${error?.response?.data?.error || error.message || 'unknown error'}`
          );
          return;
        }
      }
    }
    sendCommandViaSocket('launch_app', { package: packageName });
    setStatusMessage('Launch request sent. No configured app profile was found.');
    setLastAction(`launch_app:${packageName}`);
  }, [deviceId, sendCommandViaSocket, transport]);

  const handleLaunchSelectedApp = useCallback(() => {
    if (!selectedAppPackage) {
      setStatusMessage('Choose an app to launch first.');
      return;
    }
    launchAppPackage(selectedAppPackage);
  }, [launchAppPackage, selectedAppPackage]);

  const handleLaunchAppPackage = useCallback((packageName) => {
    const normalizedPackage = String(packageName || '').trim();
    if (!normalizedPackage) {
      setStatusMessage('Choose an app to launch first.');
      return;
    }
    setSelectedAppPackage(normalizedPackage);
    launchAppPackage(normalizedPackage);
  }, [launchAppPackage]);

  const handleExecuteContentTarget = useCallback(async (target) => {
    if (!target?.id || typeof transport?.executeContentTarget !== 'function') return;
    setTargetExecution({
      targetId: target.id,
      targetName: target.name,
      status: 'running',
      message: `Starting ${target.name}…`,
    });
    setStatusMessage(`Starting ${target.name}…`);
    appendConsoleLine('Content Target execution started.', {
      targetId: target.id,
      targetName: target.name,
    });
    try {
      const result = await transport.executeContentTarget({
        deviceId,
        contentTargetId: target.id,
      });
      const strategyId = result?.result?.strategyId || null;
      const nextExecution = {
        targetId: target.id,
        targetName: target.name,
        status: 'succeeded',
        executionId: result?.executionId || null,
        strategyId,
        message: strategyId
          ? `${target.name} started using ${strategyId}.`
          : `${target.name} started.`,
      };
      setTargetExecution(nextExecution);
      setStatusMessage(nextExecution.message);
      setLastAction(`content_target:${target.id}`);
      appendConsoleLine('Content Target execution succeeded.', {
        ...nextExecution,
        result,
      });
      queueSnapshotRefresh(inputDelayMs, 'content target');
      return result;
    } catch (err) {
      const executionId = err?.payload?.executionId || err?.response?.data?.executionId || null;
      const reason = err?.payload?.message || err?.response?.data?.message || err.message;
      const nextExecution = {
        targetId: target.id,
        targetName: target.name,
        status: 'failed',
        executionId,
        message: `${target.name} failed: ${reason}`,
      };
      setTargetExecution(nextExecution);
      setStatusMessage(nextExecution.message);
      appendConsoleLine('Content Target execution failed.', {
        ...nextExecution,
        error: err?.payload || err.message,
      });
      return null;
    }
  }, [
    appendConsoleLine,
    deviceId,
    inputDelayMs,
    queueSnapshotRefresh,
    transport,
  ]);

  const handleRefreshApps = useCallback(() => {
    if (!deviceId) return;
    sendCommandViaSocket('list_apps');
    setStatusMessage('Refreshing installed apps from the device…');
    appendConsoleLine('Requested installed app list from device.', { deviceId });
  }, [appendConsoleLine, deviceId, sendCommandViaSocket]);

  const handleSaveLgSettings = useCallback(async () => {
    await persistLgIntegration(
      {
        supportsWebos: true,
        tvControls: {
          ...(tvControlsIntegration || {}),
          ip: lgIpInput.trim() || undefined,
          mac: normalizedLgMac || undefined,
        },
      },
      'LG TV settings saved.'
    );
  }, [lgIpInput, normalizedLgMac, persistLgIntegration, tvControlsIntegration]);

  const handleLgConnect = useCallback(() => {
    if (lgTarget && transport?.sendTargetAction) {
      const requestId = beginCommandAttempt('connect', 'lg_webos_remote');
      return sendWebosCommand('connect', requestId);
    }
    const ipAddress = lgIpInput.trim();
    if (!ipAddress) {
      setLgStatusMessage('Missing IP address.');
      appendConsoleLine('LG TV connect failed: missing IP address.');
      return;
    }
    setLgStatus('connecting');
    setLgStatusMessage('Sending connect/pair request…');
    sendCommandViaSocket('tv_remote_connect', {
      ipAddress,
      macAddress: normalizedLgMac || undefined,
      mode: 'lgwebos',
    });
    appendConsoleLine('LG TV connect/pair requested.', {
      ipAddress,
      macAddress: normalizedLgMac || undefined,
    });
  }, [lgTarget, transport, beginCommandAttempt, sendWebosCommand, appendConsoleLine, lgIpInput, normalizedLgMac, sendCommandViaSocket]);

  const handleLgDisconnect = useCallback(() => {
    sendCommandViaSocket('tv_remote_disconnect', {
      mode: 'lgwebos',
    });
    setLgStatus('disconnected');
    setLgStatusMessage('Disconnect requested.');
    appendConsoleLine('LG TV disconnect requested.');
  }, [appendConsoleLine, sendCommandViaSocket]);

  const handleLgWake = useCallback(() => {
    if (!normalizedLgMac) {
      setLgStatusMessage('Missing or invalid MAC address.');
      appendConsoleLine('LG TV wake request failed: invalid MAC address.');
      return;
    }
    sendCommandViaSocket('wake_on_lan', { mac: normalizedLgMac });
    setLgStatusMessage(`Power on request sent for ${normalizedLgMac}.`);
    appendConsoleLine('LG TV wake request sent.', { mac: normalizedLgMac });
  }, [appendConsoleLine, normalizedLgMac, sendCommandViaSocket]);

  const handleLgScan = useCallback(() => {
    const requestId = sendCommandViaSocket('scan_network');
    setLgScanRequestId(requestId ? String(requestId) : null);
    setLgScanStatus('scanning');
    setLgScanMessage('Scan started. Waiting for device response…');
    appendConsoleLine('LG network scan requested.', {
      deviceId,
      requestId: requestId ? String(requestId) : null,
    });
  }, [appendConsoleLine, deviceId, sendCommandViaSocket]);

  const handleLgPairFromScan = useCallback(
    async (deviceInfo) => {
      const ipAddress = String(deviceInfo?.ip || '').trim();
      const nextMac = String(deviceInfo?.mac || '').trim();
      if (!ipAddress) return;
      setLgIpInput(ipAddress);
      setLgMacInput(nextMac);
      setSelectedMethod('webos');
      await persistLgIntegration(
        {
          supportsWebos: true,
          tvControls: {
            ...(tvControlsIntegration || {}),
            ip: ipAddress,
            mac: nextMac || undefined,
          },
        },
        'Saved LG TV details from scan results.'
      );
      setLgStatus('connecting');
      setLgStatusMessage(`Pairing requested for ${ipAddress}. Check the TV for a prompt.`);
      sendCommandViaSocket('tv_remote_connect', {
        mode: 'lgwebos',
        ipAddress,
        macAddress: normalizeMacAddress(nextMac) || undefined,
      });
      appendConsoleLine('LG TV pairing requested from network scan result.', deviceInfo);
    },
    [appendConsoleLine, persistLgIntegration, sendCommandViaSocket, tvControlsIntegration]
  );

  const handleConfiguredTvSource = useCallback(async (commandKey) => {
    const televisionDeviceId = commandKey === 'input' ? Number(deviceId)
      : resolveSourceTargetDeviceId(sourceLayout, deviceId);
    const action = commandKey === 'input' ? 'input'
      : resolveWatchAction({ action: commandKey === 'tv_home' ? 'input_tv_home' : `input_${commandKey}` });
    if (!televisionDeviceId || !action || !transport?.sendAndroidTvRemoteAction) return;
    const scope = activeScopeRef.current;
    const requestId = beginCommandAttempt(action, 'configured_tv_source');
    try {
      // Omit a forced protocol so validated native/ATV input routines run first.
      const response = await transport.sendAndroidTvRemoteAction({
        deviceId: televisionDeviceId, action, configuredRoute: true, requestId,
      });
      if (scope !== activeScopeRef.current) return;
      if (!recordCommandResult(response)) {
        // The Dashboard endpoint can return a routine result without an attempt envelope.
        const attempt = createPendingCommandAttempt({ id: requestId, action, transport: 'configured_tv_source',
          target: { deviceId: televisionDeviceId, commandDeviceId: televisionDeviceId } });
        recordCommandAttempt({ ...attempt, status: response.status === 'verified' ? 'verified' : 'dispatched',
          completedAt: new Date().toISOString(),
          stages: { ...attempt.stages, server: { state: 'succeeded', detail: 'Source routine returned', latencyMs: null } },
        });
      }
      setLastAction(action);
      setStatusMessage(response.status === 'verified'
        ? 'Configured TV source routine verified.' : 'Configured TV source action sent; visible effect unverified.');
      appendConsoleLine('Configured TV source action completed.', response);
      const selectedSourceDeviceId = resolveSelectedSourceDeviceId(sourceLayout, commandKey);
      if (selectedSourceDeviceId && typeof onSourceTargetSelected === 'function') {
        onSourceTargetSelected(selectedSourceDeviceId);
      }
      queueSnapshotRefresh(inputDelayMs, 'Configured TV source action');
    } catch (error) {
      if (scope !== activeScopeRef.current) return;
      recordDeliveryFailure(error, requestId, action, 'configured_tv_source');
      setStatusMessage(error?.response?.data?.message || error.message || 'Unable to switch the connected TV input.');
    }
  }, [appendConsoleLine, beginCommandAttempt, deviceId, inputDelayMs, onSourceTargetSelected,
    queueSnapshotRefresh, recordCommandAttempt, recordCommandResult, recordDeliveryFailure, sourceLayout, transport]);

  const handleSwitchSource = useCallback(async () => {
    const assignment = hdmiAssignments.find((entry) => entry.label === selectedSource);
    const port = assignment ? assignment.port : selectedSource.match(/\d+/)?.[0];
    if (!port) {
      setStatusMessage(`Could not resolve an HDMI port from ${selectedSource}.`);
      return;
    }
    const actionKey = `switchToHDMI${port}`;

    if (!selectedMethodAvailable || selectedMethod === 'adb_remote') {
      setStatusMessage('Discrete HDMI is unavailable for the selected method.');
      return;
    }
    if (selectedMethod === 'android_tv_remote' || selectedMethod === 'app') {
      await handleConfiguredTvSource(`hdmi_${port}`);
      return;
    } else if (selectedMethod === 'assistant') {
      await sendAssistantCommand(actionKey);
    } else if (selectedMethod === 'cast') {
      await sendCastCommand(actionKey);
    } else {
      await sendServerCommand(actionKey, selectedMethod);
    }

    setLastAction(actionKey);
    if (typeof transport?.updateSettings === 'function') {
      transport.updateSettings({ deviceId, settings: { source: selectedSource } }).catch((err) => {
        console.error('Failed to persist source selection', err);
      });
    }
  }, [
    deviceId,
    hdmiAssignments,
    selectedMethod,
    selectedMethodAvailable,
    handleConfiguredTvSource,
    selectedSource,
    sendAssistantCommand,
    sendCastCommand,
    sendServerCommand,
    transport,
  ]);

  const handleAndroidTvInputTest = useCallback(async (commandKey) => {
    setStatusMessage('');
    const inputTargetDeviceId = deviceId;
    if (!selectedMethodAvailable || selectedMethod !== 'android_tv_remote') {
      setStatusMessage('Direct input testing requires the selected Google Remote method.');
      return;
    }
    if (SOURCE_COMMAND_KEYS.has(commandKey) && sourceLayout?.televisionDeviceId
        && Number(sourceLayout.televisionDeviceId) !== Number(deviceId)) {
      setStatusMessage('Use the separately named TV source action for the connected television.');
      return;
    }
    const action = ANDROID_TV_REMOTE_ACTION_MAP[commandKey];
    if (!action) {
      setStatusMessage('That HDMI input test is not mapped.');
      return;
    }
    const requestId = beginCommandAttempt(action, 'android_tv_remote');
    const result = await sendAndroidTvRemoteCommand(action, requestId, inputTargetDeviceId);
    if (!result) return;
    setLastAction(action);
    const selectedSourceDeviceId = Number(sourceLayout?.televisionDeviceId) === Number(deviceId)
      ? resolveSelectedSourceDeviceId(sourceLayout, commandKey) : null;
    if (selectedSourceDeviceId && typeof onSourceTargetSelected === 'function') {
      onSourceTargetSelected(selectedSourceDeviceId);
    }
    if (commandKey === 'tv_home') {
      setStatusMessage(result.status === 'verified'
        ? 'TV Home was verified.'
        : 'TV Home command sent; visible effect unverified.');
      queueSnapshotRefresh(inputDelayMs, 'TV Home input test');
      return;
    }
    setStatusMessage('Command sent; checking the active TV input…');

    if (typeof transport?.requestState !== 'function') {
      setStatusMessage(evaluateHdmiInputVerification(commandKey, null).message);
      return;
    }

    let verification = evaluateHdmiInputVerification(commandKey, null);
    for (let attempt = 0; attempt < HDMI_INPUT_VERIFICATION_ATTEMPTS; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, HDMI_INPUT_VERIFICATION_DELAY_MS));
      try {
        const state = await transport.requestState({ deviceId: inputTargetDeviceId, includeScreenshot: false });
        verification = evaluateHdmiInputVerification(commandKey, state);
        if (verification.status === 'confirmed' || !verification.requestedPort) break;
      } catch (err) {
        verification = evaluateHdmiInputVerification(commandKey, null);
      }
    }

    setStatusMessage(verification.message);
    appendConsoleLine('HDMI input verification completed.', verification);
    queueSnapshotRefresh(inputDelayMs, 'HDMI input test');
  }, [
    appendConsoleLine,
    beginCommandAttempt,
    deviceId,
    inputDelayMs,
    queueSnapshotRefresh,
    onSourceTargetSelected,
    selectedMethod,
    sendAndroidTvRemoteCommand,
    sourceLayout?.televisionDeviceId,
    selectedMethodAvailable,
    transport,
  ]);

  const handlePrimaryRemoteCommand = useCallback((commandKey, input) => {
    if (selectedMethodAvailable && ['android_tv_remote', 'app'].includes(selectedMethod)
        && (SOURCE_COMMAND_KEYS.has(commandKey) || commandKey === 'input')) {
      if (SOURCE_COMMAND_KEYS.has(commandKey) && sourceLayout?.televisionDeviceId
          && Number(sourceLayout.televisionDeviceId) !== Number(deviceId)) {
        setStatusMessage('Use the separately named TV source action for the connected television.');
        return undefined;
      }
      return handleConfiguredTvSource(commandKey);
    }
    return handleCommand(commandKey, input);
  }, [deviceId, handleConfiguredTvSource, handleCommand, selectedMethod, selectedMethodAvailable,
    sourceLayout?.televisionDeviceId]);

  const handleWatchSource = useCallback(() => {
    const action = resolveWatchAction(watchTarget);
    if (action) return handleConfiguredTvSource(action === 'input_tv_home' ? 'tv_home' : action.replace('input_', ''));
    return undefined;
  }, [handleConfiguredTvSource, watchTarget]);

  const connectedMethodLabels = useMemo(
    () =>
      connectedMethods.map((method) => {
        if (method === 'accessibility') return METHOD_LABELS.app;
        return METHOD_LABELS[method] || method;
      }),
    [connectedMethods]
  );

  return {
    availableByAction,
    appsStatus,
    blackFrameStreak,
    canPowerOff,
    canPowerOn,
    canPreviewSnapshot,
    canSwitchSource,
    connectedMethodLabels,
    commandAttemptHistory,
    contentTargetGroups,
    contentTargets,
    contentTargetsStatus,
    consoleLines,
    deviceId,
    deviceName,
    fetchLiveSnapshot,
    handleBackgroundIntervalChange,
    handleAndroidTvInputTest,
    handleCommand,
    handlePrimaryRemoteCommand,
    cancelRemoteInput,
    handleRemoteInputEnd: selectedMethod === 'adb_remote' ? handleRemoteInputEnd : undefined,
    handleConfiguredTvSource,
    handleExecuteContentTarget,
    handleInputDelayChange,
    handleLaunchAppPackage,
    handleLaunchSelectedApp,
    handleLaunchSuitestream,
    handleLgConnect,
    handleLgDisconnect,
    handleLgPairFromScan,
    handleLgScan,
    handleLgWake,
    handlePowerOff,
    handlePowerOn,
    handlePreviewPresetChange,
    handleRefreshApps,
    handleSaveLgSettings,
    handleSleepOnLan,
    handleSwitchSource,
    handleWakeOnLan,
    handleWatchSource,
    hdmiAssignments,
    hdmiSources,
    tvHomeSource,
    backgroundIntervalMs,
    inputDelayMs,
    isAppDevice,
    isPageVisible,
    lastAction,
    lastCommandAttempt,
    lgIpInput,
    lgLastAction,
    lgMacInput,
    lgRemoteReady,
    lgSaving,
    lgScanMessage,
    lgScanResults,
    lgScanStatus,
    lgStatus,
    lgStatusMessage,
    liveSnapshot,
    liveSnapshotLabel,
    loadApps,
    loadContentTargets,
    methodOptions,
    selectedMethodAvailable,
    normalizedLgMac,
    normalizedWolMac,
    orderedApps,
    installedExternalAppCount,
    launchableExternalAppCount,
    pendingSnapshotReasonRef,
    persistWolMac,
    previewPreset,
    protectedVideoLikely,
    remoteAvailableCommands,
    retryLastCommand,
    selectedAppPackage,
    selectedMethod,
    selectedSource,
    setConsoleLines,
    setLgIpInput,
    setLgMacInput,
    setSelectedAppPackage,
    setSelectedMethod,
    setSelectedSource,
    setWolMacInput,
    showWolAction,
    snapshotMessage,
    snapshotRequestInFlightRef,
    snapshotStatus,
    snapshotUnsupportedMessage,
    statusMessage,
    suitestreamApp,
    supportsAppLauncher,
    supportsPreviewSnapshot,
    targetExecution,
    watchTarget,
    wolMacInput,
    wolSaving,
  };
}

export default useSharedRemoteRuntime;
